import { useMemo, useState } from 'react';
import { MapPin, Plus, Search, Edit3, Trash2, Calendar, CheckSquare, Square } from 'lucide-react';
import { Card, SectionHeader, Badge, Button } from '@/components/ui';
import { INDIAN_CITIES } from '@/lib/data_registry';

interface CityItem {
  id: string;
  name: string;
  state: string;
  count: number;
  created_at: string;
  status: 'active' | 'archived';
}

export default function CitiesManager() {
  const [cities, setCities] = useState<CityItem[]>(() => {
    const saved = localStorage.getItem('localhub_custom_cities');
    const customList: CityItem[] = saved ? JSON.parse(saved) : [];
    const baseList: CityItem[] = INDIAN_CITIES.map((c, idx) => ({
      id: `city-${idx + 1}`,
      name: c,
      state: 'India',
      count: Math.floor(Math.random() * 80) + 12,
      created_at: new Date(Date.now() - (idx * 3600 * 18 * 1000)).toISOString().split('T')[0],
      status: 'active' as const
    }));
    // Merge custom cities to top
    const existingNames = new Set(baseList.map(b => b.name.toLowerCase()));
    const uniqueCustom = customList.filter(c => !existingNames.has(c.name.toLowerCase()));
    return [...uniqueCustom, ...baseList];
  });

  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | '7days' | '30days'>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCityName, setNewCityName] = useState('');
  const [editingItem, setEditingItem] = useState<CityItem | null>(null);

  // Filtered List
  const filtered = useMemo(() => {
    return cities.filter((c) => {
      const matchSearch = c.name.toLowerCase().includes(search.toLowerCase());
      if (!matchSearch) return false;

      if (dateFilter === '7days') {
        const d = new Date(c.created_at).getTime();
        const past = Date.now() - 7 * 24 * 3600 * 1000;
        return d >= past;
      }
      if (dateFilter === '30days') {
        const d = new Date(c.created_at).getTime();
        const past = Date.now() - 30 * 24 * 3600 * 1000;
        return d >= past;
      }
      return true;
    });
  }, [cities, search, dateFilter]);

  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map(c => c.id));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleAddCity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCityName.trim()) return;
    const newItem: CityItem = {
      id: `city-${Date.now()}`,
      name: newCityName.trim(),
      state: 'India',
      count: 0,
      created_at: new Date().toISOString().split('T')[0],
      status: 'active'
    };
    setCities(prev => {
      const updated = [newItem, ...prev];
      const customOnly = updated.filter(item => !INDIAN_CITIES.includes(item.name));
      localStorage.setItem('localhub_custom_cities', JSON.stringify(customOnly));
      return updated;
    });
    setNewCityName('');
    setShowAddModal(false);
  };

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !editingItem.name.trim()) return;
    setCities(prev => {
      const updated = prev.map(c => c.id === editingItem.id ? editingItem : c);
      const customOnly = updated.filter(item => !INDIAN_CITIES.includes(item.name));
      localStorage.setItem('localhub_custom_cities', JSON.stringify(customOnly));
      return updated;
    });
    setEditingItem(null);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setCities(prev => prev.filter(c => !selectedIds.includes(c.id)));
    setSelectedIds([]);
  };

  return (
    <div className="space-y-6 text-slate-800">
      <SectionHeader
        title="Cities Directory Management"
        subtitle={`Super Admin control panel for all ${cities.length.toLocaleString()} Indian cities and territories.`}
        right={
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4" /> Add New City
          </Button>
        }
      />

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-extrabold">Total Cities</div>
          <div className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">{cities.length.toLocaleString()}</div>
          <div className="text-xs font-bold text-emerald-600 mt-1">Imported from cities.csv</div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-extrabold">Selected Items</div>
          <div className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">{selectedIds.length}</div>
          <div className="text-xs font-bold text-slate-500 mt-1">Multi-select active</div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-extrabold">Filter Results</div>
          <div className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">{filtered.length.toLocaleString()}</div>
          <div className="text-xs font-bold text-emerald-600 mt-1">Matching search criteria</div>
        </Card>
      </div>

      {/* Control Toolbar */}
      <Card className="p-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full md:w-auto flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search city name..."
              className="w-full h-10 pl-9 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 outline-none focus:border-emerald-600"
            />
            {search.trim() && filtered.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-2 max-h-48 overflow-y-auto">
                <div className="text-[10px] font-bold text-slate-400 px-3 py-1 uppercase">Search Suggestions ({filtered.length})</div>
                {filtered.slice(0, 8).map(city => (
                  <div
                    key={city.id}
                    onClick={() => setSearch(city.name)}
                    className="px-3 py-1.5 hover:bg-emerald-50 rounded-lg text-xs font-bold text-slate-800 cursor-pointer flex items-center justify-between"
                  >
                    <span>{city.name}</span>
                    <span className="text-[10px] text-emerald-700">{city.count} listings</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl p-1 text-xs">
            <button
              onClick={() => setDateFilter('all')}
              className={`px-3 py-1 rounded-lg font-bold transition cursor-pointer ${dateFilter === 'all' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600'}`}
            >
              All Time
            </button>
            <button
              onClick={() => setDateFilter('7days')}
              className={`px-3 py-1 rounded-lg font-bold transition cursor-pointer ${dateFilter === '7days' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600'}`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setDateFilter('30days')}
              className={`px-3 py-1 rounded-lg font-bold transition cursor-pointer ${dateFilter === '30days' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600'}`}
            >
              Last 30 Days
            </button>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-600">{selectedIds.length} selected</span>
            <Button variant="danger" size="sm" onClick={handleBulkDelete}>
              <Trash2 className="h-3.5 w-3.5" /> Delete Selected
            </Button>
          </div>
        )}
      </Card>

      {/* Cities Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto max-h-[600px] scrollbar-thin">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider sticky top-0 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <button onClick={toggleSelectAll} className="text-slate-400 hover:text-slate-800">
                    {selectedIds.length === filtered.length && filtered.length > 0 ? <CheckSquare className="h-4 w-4 text-emerald-600" /> : <Square className="h-4 w-4" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left">City Name</th>
                <th className="px-4 py-3 text-left">Country / Region</th>
                <th className="px-4 py-3 text-right">Active Businesses</th>
                <th className="px-4 py-3 text-center">Created Date</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-semibold text-slate-800">
              {filtered.slice(0, 100).map((city) => {
                const isSelected = selectedIds.includes(city.id);
                return (
                  <tr key={city.id} className={`hover:bg-slate-50 transition ${isSelected ? 'bg-emerald-50/40' : ''}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSelect(city.id)} className="text-slate-400 hover:text-slate-800">
                        {isSelected ? <CheckSquare className="h-4 w-4 text-emerald-600" /> : <Square className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-900 flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span>{city.name}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-medium">{city.state}</td>
                    <td className="px-4 py-3 text-right font-mono font-extrabold text-emerald-700">{city.count}</td>
                    <td className="px-4 py-3 text-center text-slate-500 font-mono">{city.created_at}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge tone="success">Active</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditingItem(city)}
                          className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                          title="Edit City"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setCities(prev => prev.filter(c => c.id !== city.id))}
                          className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition"
                          title="Delete City"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-slate-200 shadow-xl space-y-4">
            <h3 className="text-base font-bold text-slate-900">Add New City</h3>
            <form onSubmit={handleAddCity} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">City Name *</label>
                <input
                  type="text"
                  value={newCityName}
                  onChange={(e) => setNewCityName(e.target.value)}
                  placeholder="e.g. Coimbatore"
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-emerald-600"
                  required
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="ghost" type="button" onClick={() => setShowAddModal(false)}>Cancel</Button>
                <Button type="submit">Create City</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-slate-200 shadow-xl space-y-4">
            <h3 className="text-base font-bold text-slate-900">Edit City</h3>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">City Name *</label>
                <input
                  type="text"
                  value={editingItem.name}
                  onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-emerald-600"
                  required
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="ghost" type="button" onClick={() => setEditingItem(null)}>Cancel</Button>
                <Button type="submit">Save Changes</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
